import { faChevronDown, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/shadcn/popover';
import { cn } from '@shared/utils/utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HoldedChartAccount } from '../../models/Wallet';

const DEBOUNCE_MS = 200;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 200;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Línea principal: número y nombre (sin grupo). */
function formatAccountPrimary(acc: HoldedChartAccount): string {
  const parts = [acc.num, acc.name].filter(Boolean);
  return parts.join(' · ') || '—';
}

function normalizeCode(n: unknown): string {
  return n != null && String(n).trim() !== '' ? String(n).trim() : '';
}

/** Valor que se persiste al elegir cuenta: id (Holded) o código contable (`num`). */
function getPersistedValue(acc: HoldedChartAccount, storedValueField: 'id' | 'num'): string {
  if (storedValueField === 'num') {
    const code = normalizeCode(acc.num);
    return code || acc.id;
  }
  return acc.id;
}

function findAccountByPersistedValue(
  value: string | undefined,
  accounts: HoldedChartAccount[],
  storedValueField: 'id' | 'num',
): HoldedChartAccount | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (storedValueField === 'num') {
    return accounts.find((a) => normalizeCode(a.num) === v || a.id === v);
  }
  return accounts.find((a) => a.id === v);
}

export interface HoldedChartAccountComboboxProps {
  accounts: HoldedChartAccount[];
  value: string;
  onValueChange: (value: string) => void;
  /** `id`: Mongo id de Holded (movimientos manuales). `num`: código de cuenta (p. ej. facturación cliente). */
  storedValueField?: 'id' | 'num';
  disabled?: boolean;
  loading?: boolean;
  dataTestId?: string;
}

export const HoldedChartAccountCombobox = ({
  accounts,
  value,
  onValueChange,
  storedValueField = 'id',
  disabled = false,
  loading = false,
  dataTestId = 'holded-chart-account-combobox',
}: HoldedChartAccountComboboxProps) => {
  const { t } = useTranslation('walletsModule');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, DEBOUNCE_MS);

  const inputRef = useRef<HTMLInputElement>(null);
  const listParentRef = useRef<HTMLDivElement>(null);

  const filteredAccounts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    const out: HoldedChartAccount[] = [];
    for (const acc of accounts) {
      const hay = [acc.num, acc.name, acc.group, acc.id].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) {
        out.push(acc);
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  }, [accounts, debouncedSearch]);

  const selectedAccount = useMemo(
    () => findAccountByPersistedValue(value, accounts, storedValueField),
    [accounts, value, storedValueField],
  );

  const virtualizer = useVirtualizer({
    count: filteredAccounts.length,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) {
      setSearchQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, []);

  const handleSelect = useCallback(
    (acc: HoldedChartAccount) => {
      onValueChange(getPersistedValue(acc, storedValueField));
      setOpen(false);
      setSearchQuery('');
    },
    [onValueChange, storedValueField],
  );

  return (
    <Popover open={open && !disabled} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            'h-auto min-h-10 w-full justify-between py-2 px-3 font-normal whitespace-normal',
            '[&>span]:line-clamp-none',
          )}
          data-testid={dataTestId}
        >
          <span className="truncate text-left text-sm flex-1 min-w-0">
            {loading ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                {t('manualTransaction.loadingAccounts')}
              </span>
            ) : selectedAccount ? (
              <span className="flex flex-col items-start gap-0.5 text-left w-full">
                <span className="text-foreground font-medium line-clamp-2">
                  {formatAccountPrimary(selectedAccount)}
                </span>
                {selectedAccount.group ? (
                  <span className="text-xs text-muted-foreground line-clamp-1 w-full">{selectedAccount.group}</span>
                ) : null}
              </span>
            ) : value ? (
              <span className="text-xs font-mono text-foreground break-all text-left">{value}</span>
            ) : (
              <span className="text-muted-foreground">{t('manualTransaction.placeholders.holdedAccount')}</span>
            )}
          </span>
          <FontAwesomeIcon icon={faChevronDown} className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 border-border bg-popover text-popover-foreground"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col max-h-80">
          <div className="flex items-center border-b border-border px-3 py-2">
            <input
              ref={inputRef}
              type="search"
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('manualTransaction.holdedCombobox.searchPlaceholder')}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid={`${dataTestId}-search`}
            />
          </div>

          <div
            ref={listParentRef}
            className="overflow-auto flex-1"
            style={{ height: 280 }}
            data-testid={`${dataTestId}-list`}
          >
            {debouncedSearch.trim().length >= MIN_SEARCH_LENGTH && filteredAccounts.length === 0 && !loading && (
              <div className="py-8 text-center text-sm text-muted-foreground px-3">
                {t('manualTransaction.holdedCombobox.noMatches')}
              </div>
            )}

            {debouncedSearch.trim().length >= MIN_SEARCH_LENGTH && filteredAccounts.length > 0 && (
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const acc = filteredAccounts[virtualItem.index];
                  const persisted = getPersistedValue(acc, storedValueField);
                  const isSelected = persisted === value.trim() || acc.id === value.trim();
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      className={cn(
                        'absolute top-0 left-0 w-full px-3 py-2 text-left text-sm transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent/60',
                      )}
                      style={{ transform: `translateY(${virtualItem.start}px)` }}
                      onClick={() => handleSelect(acc)}
                    >
                      <span className="flex flex-col items-start gap-0.5 text-left w-full">
                        <span className="font-medium line-clamp-2">{formatAccountPrimary(acc)}</span>
                        {acc.group ? (
                          <span className="text-xs text-muted-foreground line-clamp-2">{acc.group}</span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Estado inicial: sin búsqueda aún */}
            {debouncedSearch.trim().length < MIN_SEARCH_LENGTH && (
              <div className="py-8 text-center text-sm text-muted-foreground px-3">
                {t('manualTransaction.holdedCombobox.hintStart')}
              </div>
            )}
          </div>

          {filteredAccounts.length >= MAX_RESULTS && debouncedSearch.trim().length >= MIN_SEARCH_LENGTH && (
            <p className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
              {t('manualTransaction.holdedCombobox.capped', { max: MAX_RESULTS })}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
